/**
 * Google Multi-Gen Model Integration Service for "The Caesura Engine"
 * Provides:
 * 1. Live Voice Narration:
 *    - Google Gemini 2.0 Flash Audio API (native multimodal voice: Fenrir, Charon, Puck, Aoede, Kore)
 *    - Google Cloud Text-to-Speech API (DeepMind Journey-D / Studio-M)
 *    - Antigravity Tools / Universal Bridge Gemini 3.8 Flash in-character script synthesis
 *    - Local Atmospheric Web Speech DSP with Web Audio helmet communications filter
 * 2. Live Image Generation:
 *    - Antigravity Tools (port 8045) Gemini 3.1 Flash Image API (photorealistic 8K dark sci-fi C2PA SynthID generation)
 *    - Google Imagen 3.0 / Gemini Multimodal API via API Key / Google OAuth
 *    - Intelligent Procedural Canvas & SVG Matrix fallback
 * 3. Live Lore Analysis & Jump Telemetry Commentary:
 *    - In-character Marcus Ward analysis powered by Google Gemini 3.8 Flash / 3.1 Pro High
 */

import { getAudioEngine } from '../audio/AudioEngine';
import { formatPageParagraphs } from '../utils/proseFormatter';
import { getPage, TOTAL_PAGES } from '../data/tome';
import { CHARACTER_ACOUSTIC_DOSSIERS } from '../data/characterAcousticDossiers';

export type AIProviderMode =
  | 'antigravity-tools'
  | 'universal-bridge'
  | 'google-cloud-key'
  | 'google-oauth'
  | 'offline-dsp';

export type GoogleVoiceModel =
  | 'elevenlabs'
  | 'neural-generative'
  | 'gemini-2.0-flash'
  | 'google-cloud-tts'
  | 'kokoro-82m'
  | 'antigravity-gemini'
  | 'web-speech-dsp';

export type GoogleGeminiVoice = 'Fenrir' | 'Charon' | 'Puck' | 'Aoede' | 'Kore';

export type CharacterGender = 'male' | 'female' | 'entity';

export interface CharacterVoicePreset {
  id: string;
  name: string;
  characterTitle: string;
  gender: CharacterGender;
  neuralVoice: string;
  geminiVoice: GoogleGeminiVoice;
  voiceSearchPatterns: string[];
  preferredVoiceName?: string;
  voicePitch: number;
  voicePace: number;
  chestResonance: number;
  commsGrit: number;
  helmetBandwidth: number;
  cabinReverb: number;
  squelchClicks: boolean;
  testPhrase: string;
}

export const CHARACTER_VOICE_PRESETS: Record<string, CharacterVoicePreset> = {
  narrator: {
    id: 'narrator',
    name: 'Elder Narrator',
    characterTitle: 'The Veteran Chronicler (Smoky 60s Male, Locked in the Void)',
    gender: 'male',
    neuralVoice: 'en-US-RogerNeural',
    geminiVoice: 'Fenrir',
    voiceSearchPatterns: ['Roger', 'David', 'Mark', 'Guy', 'George', 'Male'],
    voicePitch: 0.65,
    voicePace: 0.78,
    chestResonance: 0.98,
    commsGrit: 0.75,
    helmetBandwidth: 1200,
    cabinReverb: 0.45,
    squelchClicks: false,
    testPhrase: "I have watched seventy-four starships unthread into the silence. In the margin between seconds, there are no clocks. Only the remembering.",
  },
  marcus_ward: {
    id: 'marcus_ward',
    name: 'Marcus Ward',
    characterTitle: 'Senior Jump Engineer (Gravelly Weary Baritone)',
    gender: 'male',
    neuralVoice: 'en-US-ChristopherNeural',
    geminiVoice: 'Fenrir',
    voiceSearchPatterns: ['David', 'Mark', 'Guy', 'George', 'Male', 'Natural'],
    voicePitch: 0.72,
    voicePace: 0.82,
    chestResonance: 0.92,
    commsGrit: 0.70,
    helmetBandwidth: 1300,
    cabinReverb: 0.25,
    squelchClicks: true,
    testPhrase: "Forty cycles on the Geryon keel. You don't jump through space, kid... you let the ledger unthread you.",
  },
  elena_cruz: {
    id: 'elena_cruz',
    name: 'Elena Cruz',
    characterTitle: 'Chief Jump Architect (Analytical & Cold Precision)',
    gender: 'female',
    neuralVoice: 'en-US-AriaNeural',
    geminiVoice: 'Aoede',
    voiceSearchPatterns: ['Zira', 'Jenny', 'Samantha', 'Victoria', 'Aria', 'Female'],
    voicePitch: 1.08,
    voicePace: 1.04,
    chestResonance: 0.15,
    commsGrit: 0.15,
    helmetBandwidth: 2600,
    cabinReverb: 0.12,
    squelchClicks: true,
    testPhrase: "Coordinates locked on Perseus Prime. Vector four-dash-gamma. All primary choke valves sealed. Initiate the Planck step.",
  },
  dr_simon_peter: {
    id: 'dr_simon_peter',
    name: 'Dr. Simon Peter',
    characterTitle: 'Psychiatric Officer (Soft Clinical Distance)',
    gender: 'male',
    neuralVoice: 'en-GB-RyanNeural',
    geminiVoice: 'Kore',
    voiceSearchPatterns: ['Mark', 'David', 'Guy', 'George', 'Male'],
    voicePitch: 0.88,
    voicePace: 0.84,
    chestResonance: 0.35,
    commsGrit: 0.10,
    helmetBandwidth: 1900,
    cabinReverb: 0.65,
    squelchClicks: false,
    testPhrase: "The aphasia begins with simple nouns. By cycle thirty, you forget the shape of your mother's hand.",
  },
  cadet_silas: {
    id: 'cadet_silas',
    name: 'Cadet Silas Vance',
    characterTitle: 'Junior Technician (Nervous Comms Flutter)',
    gender: 'male',
    neuralVoice: 'en-US-SteffanNeural',
    geminiVoice: 'Puck',
    voiceSearchPatterns: ['Mark', 'David', 'Guy', 'George', 'Male'],
    voicePitch: 1.18,
    voicePace: 1.12,
    chestResonance: 0.10,
    commsGrit: 0.50,
    helmetBandwidth: 1550,
    cabinReverb: 0.18,
    squelchClicks: true,
    testPhrase: "Chief? The reactor telemetry is—wait, the second hand on my chrono just jumped backwards!",
  },
  inquisitor_thorne: {
    id: 'inquisitor_thorne',
    name: 'Inquisitor Thorne',
    characterTitle: 'Combine Tribunal (Deep Menacing Dread)',
    gender: 'male',
    neuralVoice: 'en-US-GuyNeural',
    geminiVoice: 'Charon',
    voiceSearchPatterns: ['David', 'Mark', 'Guy', 'George', 'Male'],
    voicePitch: 0.52,
    voicePace: 0.72,
    chestResonance: 1.00,
    commsGrit: 0.85,
    helmetBandwidth: 900,
    cabinReverb: 0.60,
    squelchClicks: true,
    testPhrase: "By authority of the Combine Merchant Registry: unseal the Black Box. The Caesura claims its ledger.",
  },
  sister_vera: {
    id: 'sister_vera',
    name: 'Sister Vera Kahl',
    characterTitle: 'Tribunal Inquisitress (Choral Dual-Reed Organ Harmonic)',
    gender: 'female',
    neuralVoice: 'en-US-JennyMultilingualNeural',
    geminiVoice: 'Aoede',
    voiceSearchPatterns: ['Jenny', 'Zira', 'Glinda', 'Female'],
    voicePitch: 0.95,
    voicePace: 0.85,
    chestResonance: 0.40,
    commsGrit: 0.30,
    helmetBandwidth: 2400,
    cabinReverb: 0.80,
    squelchClicks: false,
    testPhrase: "All flesh caught within the bevel belongs to the Reliquary, Chief Ward. Step back from the copper, or we will tune your marrow to the dark.",
  },
  tick_tock_benny: {
    id: 'tick_tock_benny',
    name: 'Tick-Tock Benny',
    characterTitle: 'Dorsal Hawser-Walker (Manic 53ps Pre-Cognition)',
    gender: 'male',
    neuralVoice: 'en-US-JasonNeural',
    geminiVoice: 'Puck',
    voiceSearchPatterns: ['Mark', 'Jason', 'Callum', 'Male'],
    voicePitch: 1.15,
    voicePace: 1.25,
    chestResonance: 0.20,
    commsGrit: 0.75,
    helmetBandwidth: 1400,
    cabinReverb: 0.15,
    squelchClicks: true,
    testPhrase: "Don't clamp it on the up-stroke, Chief! For God's sake, don't touch the bus while the stars are lit! You'll vaporize the whole dorsal gantry!",
  },
  mama_jo: {
    id: 'mama_jo',
    name: 'Mama Jo Vane',
    characterTitle: 'Kilo-Six Quartermaster (Smoky Contralto Welder)',
    gender: 'female',
    neuralVoice: 'en-US-NancyNeural',
    geminiVoice: 'Aoede',
    voiceSearchPatterns: ['Nancy', 'Jessie', 'Female'],
    voicePitch: 0.82,
    voicePace: 0.90,
    chestResonance: 0.85,
    commsGrit: 0.60,
    helmetBandwidth: 1800,
    cabinReverb: 0.35,
    squelchClicks: true,
    testPhrase: "Marcus! Whatever the fuck you're doing back at Kilo-Eleven, do it fast! The Vault doors at Kilo-Nine just blew open!",
  },
  margin_spectator: {
    id: 'margin_spectator',
    name: 'The Margin Spectator',
    characterTitle: 'Entity in the Pause (Otherworldly Whisper)',
    gender: 'entity',
    neuralVoice: 'en-US-AndrewMultilingualNeural',
    geminiVoice: 'Charon',
    voiceSearchPatterns: ['David', 'Mark', 'Natural', 'Male'],
    voicePitch: 0.58,
    voicePace: 0.68,
    chestResonance: 0.95,
    commsGrit: 0.90,
    helmetBandwidth: 1100,
    cabinReverb: 0.88,
    squelchClicks: false,
    testPhrase: "Between the tick and the tock... we count every atom you left behind.",
  },
  vox9_telemetry: {
    id: 'vox9_telemetry',
    name: 'VOX-9 Navigation Computer',
    characterTitle: 'Automated Shipboard Nav-Telemetry AI (Flat Synthetic Autopilot)',
    gender: 'entity',
    neuralVoice: 'en-US-AvaNeural',
    geminiVoice: 'Puck',
    voiceSearchPatterns: ['Ava', 'Zira', 'Female', 'Natural'],
    voicePitch: 1.00,
    voicePace: 1.05,
    chestResonance: 0.05,
    commsGrit: 0.05,
    helmetBandwidth: 2200,
    cabinReverb: 0.0,
    squelchClicks: false,
    testPhrase: "CHRONO-STEP ZERO-POINT-ZERO-ZERO-THREE. DOUBLE FORWARD SLASH NULL. SECTOR SEVEN-SEVEN-SEVEN RE-ENTRY LOCKED.",
  },
};

export interface GoogleGenAIConfig {
  apiKey: string;
  providerMode: AIProviderMode;
  proxyUrl: string; // e.g. 'http://127.0.0.1:8045/v1' or 'http://localhost:8056/v1'
  proxyApiKey: string; // e.g. 'sk-74fd5d2057004ec0ac2be093febdec65'
  oauthToken: string; // Google OAuth Bearer Token (ya29...)
  voiceModel: GoogleVoiceModel;
  geminiVoice: GoogleGeminiVoice;
  chatModel: 'gemini-3.8-flash' | 'gemini-3.1-pro-high' | 'gemini-2.5-flash';
  imageModel:
    | 'antigravity-gemini-image'
    | 'imagen-3.0-generate-002'
    | 'gemini-2.0-flash'
    | 'procedural-vector';
  narratorPace: number; // legacy alias
  narratorPitch: number; // legacy alias
  atmosphericFilter: boolean;
  // Advanced Vocal DSP Rack
  voicePitch: number; // 0.60 to 1.40
  voicePace: number; // 0.60 to 1.40
  chestResonance: number; // 0.0 to 1.0
  commsGrit: number; // 0.0 to 1.0
  helmetBandwidth: number; // 800 to 3200
  cabinReverb: number; // 0.0 to 1.0
  squelchClicks: boolean;
  characterPreset: string;
  preferredVoiceName?: string;
  multiVoiceNarration?: boolean;
  enableReadAlongHighlight?: boolean;
  marginaliaReadMode?: 'skip' | 'auto' | 'on-demand';
  masteringProfile?: 'studio_master' | 'comm_link';
  elevenLabsApiKey?: string;
  elevenLabsModel?: string;
  characterStateId?: string;
}

export const DEFAULT_AI_CONFIG: GoogleGenAIConfig = {
  apiKey:
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GEMINI_API_KEY) ||
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_API_KEY) ||
    '',
  providerMode: 'antigravity-tools',
  proxyUrl: 'http://127.0.0.1:8045/v1',
  proxyApiKey: 'sk-74fd5d2057004ec0ac2be093febdec65',
  oauthToken: '',
  voiceModel: 'neural-generative',
  geminiVoice: 'Fenrir',
  chatModel: 'gemini-3.8-flash',
  imageModel: 'antigravity-gemini-image',
  narratorPace: 0.88,
  narratorPitch: 0.76,
  atmosphericFilter: true,
  voicePitch: 0.76,
  voicePace: 0.88,
  chestResonance: 0.85,
  commsGrit: 0.65,
  helmetBandwidth: 1400,
  cabinReverb: 0.25,
  squelchClicks: false,
  characterPreset: 'marcus_ward',
  preferredVoiceName: '',
  multiVoiceNarration: true,
  enableReadAlongHighlight: true,
  marginaliaReadMode: 'skip',
  masteringProfile: 'studio_master',
  elevenLabsApiKey:
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_ELEVENLABS_API_KEY) ||
    (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_XI_API_KEY) ||
    '',
  elevenLabsModel: 'eleven_multilingual_v2',
  characterStateId: 'routine',
};

const STORAGE_KEY_AI_CONFIG = 'caesura_google_genai_config';
const STORAGE_KEY_GENERATED_ART = 'caesura_generated_art_cache';
const STORAGE_KEY_COMMENTARY_CACHE = 'caesura_commentary_cache';

export interface CharacterStateScript {
  stateId: string;
  stateName: string;
  voiceName: string;
  voiceId: string;
  stability: number;
  similarity: number;
  style: number;
  boost: boolean;
  script: string;
  targetFile?: string;
}

export const ELEVENLABS_CHARACTER_STATES: Record<string, CharacterStateScript[]> = {
  marcus_ward: [
    {
      stateId: 'routine',
      stateName: 'Routine Diagnostics',
      voiceName: 'Adam',
      voiceId: 'pNInz6obpgDQGcFmaJgB',
      stability: 0.65,
      similarity: 0.80,
      style: 0.25,
      boost: true,
      script: "Ward here, Lead Diagnostics. Hydros on the fifty-three picosecond bevel are running two degrees hot, but the copper seals are holding. Silas, torque down the bypass manifold on the three-bus before the next down-stroke. Just another routine cycle into the dark.",
      targetFile: 'marcus_ward_routine.mp3',
    },
    {
      stateId: 'rage',
      stateName: 'Gantry Scream / Rage',
      voiceName: 'Adam',
      voiceId: 'pNInz6obpgDQGcFmaJgB',
      stability: 0.35,
      similarity: 0.75,
      style: 0.75,
      boost: true,
      script: "Silas! Don't you dare touch that breaker! Jan Kovacs's forearm is pinned across the shear-line! If that hydraulic ram seats on the down-stroke, it pulverizes his marrow into the unrendered wake! Ground your boots to the rail and grab the cheater bar NOW!",
      targetFile: 'marcus_ward_rage.mp3',
    },
    {
      stateId: 'dying',
      stateName: 'Hypoxic / Dying Synapse',
      voiceName: 'Adam',
      voiceId: 'pNInz6obpgDQGcFmaJgB',
      stability: 0.38,
      similarity: 0.70,
      style: 0.60,
      boost: true,
      script: "Elena... the fifty-three picoseconds... aren't empty. We never bypassed the ledger... it took my ribs... Tell Silas... don't let them unrender the boys... The void has teeth...",
      targetFile: 'marcus_ward_dying.mp3',
    },
  ],
  narrator: [
    {
      stateId: 'prologue',
      stateName: 'Cosmic Ledger Prologue',
      voiceName: 'Brian',
      voiceId: 'nPczCjzI2devNBz1zQrb',
      stability: 0.68,
      similarity: 0.85,
      style: 0.20,
      boost: true,
      script: "The universe runs on a dirty sixty-four-bit clock, and it takes four point two petawatts of pig-iron violence to club that clock between the eyes. Out past the sulfur bands of Jupiter, twelve kilometers of blackened leviathan thundered on toward the Ceres dark-buoy, dragging chained mountains through the dark.",
      targetFile: 'elder_narrator_prologue.mp3',
    },
    {
      stateId: 'momentum',
      stateName: 'Titanium Acceleration',
      voiceName: 'Brian',
      voiceId: 'nPczCjzI2devNBz1zQrb',
      stability: 0.50,
      similarity: 0.80,
      style: 0.40,
      boost: true,
      script: "Seven hundred thousand metric tons of raw pig-iron asteroid slag screamed against their magnetic shackles. The USSC Geryon was no research needle—she was an iron mountain with five fusion engines bolted to her spine.",
      targetFile: 'elder_narrator_acceleration.mp3',
    },
    {
      stateId: 'void',
      stateName: 'The Quiet Margin',
      voiceName: 'Brian',
      voiceId: 'nPczCjzI2devNBz1zQrb',
      stability: 0.60,
      similarity: 0.85,
      style: 0.25,
      boost: false,
      script: "In the stillness between seconds, there are no clocks. There are no union rosters, no corporate ledgers, no names carved in steel. There is only the remembering. And what watches from the dark.",
      targetFile: 'elder_narrator_quiet_margin.mp3',
    },
  ],
  elena_cruz: [
    {
      stateId: 'command',
      stateName: 'Corporate Command',
      voiceName: 'Rachel',
      voiceId: '21m00Tcm4TlvDq8ikWAM',
      stability: 0.85,
      similarity: 0.85,
      style: 0.15,
      boost: true,
      script: "Command override: Sector Four dorsal cryo-headers to full open. Cycle the Bus Four secondary phase-baffle on the fifty-third down-stroke. Flush the inspection gantry into the wake and log Chief Ward as an un-tethered thermal casualty. He has forfeited nineteen years of vested pension equity.",
      targetFile: 'elena_cruz_corporate_command.mp3',
    },
    {
      stateId: 'threat',
      stateName: 'Cold Actuarial Threat',
      voiceName: 'Rachel',
      voiceId: '21m00Tcm4TlvDq8ikWAM',
      stability: 0.72,
      similarity: 0.85,
      style: 0.55,
      boost: true,
      script: "Let me make the arithmetic perfectly clear to you, Ward. You are a carbon component in an eleven-billion-credit haul. If you hesitate for even one picosecond, I will not fire you. I will purge your identity partition from the combine registry, and your family will inherit your debt down to their fourth generation.",
      targetFile: 'elena_cruz_threat.mp3',
    },
  ],
  cadet_silas: [
    {
      stateId: 'nervous',
      stateName: 'Trembling Telemetry',
      voiceName: 'Harry',
      voiceId: 'SOYHLrjzK2X1ezoPC6cr',
      stability: 0.45,
      similarity: 0.80,
      style: 0.45,
      boost: true,
      script: "Chief? Chief Ward, are you seeing this telemetry? The mass registers in Cargo Bay Four just fluctuated by twelve million metric tons, but the pressure transducers didn't even twitch! What's happening to the numbers, Chief?",
      targetFile: 'silas_vance_nervous.mp3',
    },
    {
      stateId: 'pure_panic',
      stateName: 'Pure Unrendered Panic',
      voiceName: 'Harry',
      voiceId: 'SOYHLrjzK2X1ezoPC6cr',
      stability: 0.25,
      similarity: 0.75,
      style: 0.85,
      boost: true,
      script: "IT'S INSIDE THE BULKHEAD! The steel is turning into black glass! Marcus! Marcus, the shadow is chewing through the conduit lines! God, my hands—my fingers are flickering into wireframes! I don't want to disappear! PLEASE!",
      targetFile: 'silas_vance_pure_panic.mp3',
    },
  ],
  inquisitor_thorne: [
    {
      stateId: 'sub_bass_decree',
      stateName: 'Sub-Bass Tribunal Decree',
      voiceName: 'Daniel',
      voiceId: 'onwK4e9ZLuTAKqWW03F9',
      stability: 0.88,
      similarity: 0.85,
      style: 0.30,
      boost: true,
      script: "The dead-band is the only incorruptible court in the solar system. Drive a phase-spike into a heretic's chest during the fifty-three picoseconds, and when the universe renders Tick N plus one, the blood never spilled, the trajectory never existed, and the heart simply ceased to have ever beaten.",
      targetFile: 'inquisitor_thorne_sub_bass_decree.mp3',
    },
  ],
  margin_spectator: [
    {
      stateId: 'void_resonance',
      stateName: 'Compiler Purge Decree',
      voiceName: 'Arnold',
      voiceId: 'VR6AewLTigWG4xSOukaG',
      stability: 0.92,
      similarity: 0.80,
      style: 0.15,
      boost: true,
      script: "COORDINATE ADDRESS DETECTED: ZERO-X-NINE-NINE-TWO-B-THETA. MASS OVERDRAFT REGISTERED: SIX HUNDRED AND FORTY-EIGHT MILLION METRIC TONS. COMMENCE COMPILER PURGE.",
      targetFile: 'margin_spectator_void_resonance.mp3',
    },
  ],
  sister_vera: [
    {
      stateId: 'choral_litany',
      stateName: 'Dual-Reed Choral Litany',
      voiceName: 'Glinda',
      voiceId: 'z9fAnlkpzviPz146aGWa',
      stability: 0.68,
      similarity: 0.82,
      style: 0.45,
      boost: true,
      script: "All flesh caught within the bevel belongs to the Reliquary, Chief Ward. Do not ground that limb! The commutator requires its tithe. The unrendered dark is an obsidian altar, and you are attempting to steal God's arithmetic with a four-foot cheater bar!",
      targetFile: 'sister_vera_kahl_choral_litany.mp3',
    },
    {
      stateId: 'execution',
      stateName: 'Phase-Spike Execution',
      voiceName: 'Glinda',
      voiceId: 'z9fAnlkpzviPz146aGWa',
      stability: 0.82,
      similarity: 0.85,
      style: 0.60,
      boost: true,
      script: "By authority of the High Commercial Tribunal, your coordinate vector is condemned. In the name of the Prime Balance, receive the phase-spike into the dead-band. Render your marrow unto the silence.",
      targetFile: 'sister_vera_kahl_execution.mp3',
    },
  ],
  tick_tock_benny: [
    {
      stateId: 'manic_precog',
      stateName: 'Manic Pre-Cognitive Staccato',
      voiceName: 'Callum',
      voiceId: 'N2lVS1w4EtoT3dr4eOWO',
      stability: 0.30,
      similarity: 0.75,
      style: 0.80,
      boost: true,
      script: "Fifty-three! Fifty-three! Ha ha! He doesn't drop the wrench, Marcus—his elbow snaps backward on tick fifty-two! Whoa! Duck left, Pollock! The shear bolt is blowing its crown! Left, I told ya! Ha ha! God almighty, look at the size of the shadow swimming behind Asteroid Two!",
      targetFile: 'tick_tock_benny_manic_precog.mp3',
    },
  ],
  mama_jo: [
    {
      stateId: 'galley_dropforge',
      stateName: 'Galley Drop-Forge Queen',
      voiceName: 'Jessie',
      voiceId: 't0jbNlBVZ17f02VDIeMI',
      stability: 0.58,
      similarity: 0.80,
      style: 0.45,
      boost: true,
      script: "Fifty-third cycle coming up on the big board, boys! Hold onto your soup spoons and spit your silver in the bucket! Pollock has got forty union scrip on young Miller's back left molar popping out on the down-stroke! Come on, cosmic ledger—mama needs new copper tubing for the still!",
      targetFile: 'mama_jo_vane_galley_dropforge.mp3',
    },
  ],
  dr_simon_peter: [
    {
      stateId: 'clinical_autopsy',
      stateName: 'Clinical Post-Mortem Audit',
      voiceName: 'Brian',
      voiceId: 'nPczCjzI2devNBz1zQrb',
      stability: 0.78,
      similarity: 0.82,
      style: 0.20,
      boost: true,
      script: "Subject: Ward, Marcus. Forty cycles in the jump gantry. The electroencephalogram shows persistent seventy-five-hertz phase-bleed across both parietal lobes. He is no longer remembering his past; his synapses are registering the coordinates of tomorrow's unrendering.",
      targetFile: 'dr_simon_peter_clinical_autopsy.mp3',
    },
  ],
  vox9_telemetry: [
    {
      stateId: 'nav_lock',
      stateName: 'Autonomous Nav-Lock Telemetry',
      voiceName: 'Rachel',
      voiceId: '21m00Tcm4TlvDq8ikWAM',
      stability: 0.95,
      similarity: 0.90,
      style: 0.05,
      boost: false,
      script: "CHRONO-STEP ZERO-POINT-ZERO-ZERO-THREE. DOUBLE FORWARD SLASH NULL. SECTOR SEVEN-SEVEN-SEVEN RE-ENTRY LOCKED. ALL DORSAL FLIGHT BAFFLES CYCLED TO RED.",
      targetFile: 'vox9_nav_lock.mp3',
    },
  ],
};

export function loadAIConfig(): GoogleGenAIConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_AI_CONFIG);
    if (!raw) return DEFAULT_AI_CONFIG;
    const parsed = JSON.parse(raw);
    // Upgrade legacy or robotic voiceModel settings to neural-generative
    if (!parsed.voiceModel || parsed.voiceModel === 'web-speech-dsp') {
      parsed.voiceModel = 'neural-generative';
    }
    if (parsed.enableReadAlongHighlight === undefined) {
      parsed.enableReadAlongHighlight = true;
    }
    if (parsed.marginaliaReadMode === undefined) {
      parsed.marginaliaReadMode = 'skip';
    }
    if (parsed.masteringProfile === undefined) {
      parsed.masteringProfile = 'studio_master';
    }
    return { ...DEFAULT_AI_CONFIG, ...parsed };
  } catch {
    return DEFAULT_AI_CONFIG;
  }
}

export function saveAIConfig(config: GoogleGenAIConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY_AI_CONFIG, JSON.stringify(config));
  } catch (err) {
    console.warn('[GoogleGenAI] Failed to save config:', err);
  }
}

export function loadGeneratedArtCache(): Record<number, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_GENERATED_ART);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveGeneratedArtCache(pageNum: number, imageUrl: string): void {
  try {
    const current = loadGeneratedArtCache();
    current[pageNum] = imageUrl;
    localStorage.setItem(STORAGE_KEY_GENERATED_ART, JSON.stringify(current));
  } catch (err) {
    console.warn('[GoogleGenAI] Failed to save generated art:', err);
  }
}

export function loadCommentaryCache(): Record<number, string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_COMMENTARY_CACHE);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export function saveCommentaryCache(pageNum: number, commentary: string): void {
  try {
    const current = loadCommentaryCache();
    current[pageNum] = commentary;
    localStorage.setItem(STORAGE_KEY_COMMENTARY_CACHE, JSON.stringify(current));
  } catch (err) {
    console.warn('[GoogleGenAI] Failed to save commentary:', err);
  }
}

/**
 * Decodes base64 string into Uint8Array safely across browser environments.
 */
export function base64ToUint8Array(base64: string): Uint8Array {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

/**
 * Packages raw 16-bit mono/stereo PCM samples into a standard 44-byte RIFF WAV container buffer.
 */
export function pcmToWav(pcmData: Uint8Array, sampleRate = 24000, numChannels = 1): ArrayBuffer {
  const header = new ArrayBuffer(44);
  const view = new DataView(header);

  // "RIFF"
  view.setUint32(0, 0x52494646, false);
  view.setUint32(4, 36 + pcmData.length, true);
  // "WAVE"
  view.setUint32(8, 0x57415645, false);
  // "fmt "
  view.setUint32(12, 0x666d7420, false);
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true); // byte rate
  view.setUint16(32, numChannels * 2, true); // block align
  view.setUint16(34, 16, true); // 16-bit
  // "data"
  view.setUint32(36, 0x64617461, false);
  view.setUint32(40, pcmData.length, true);

  const wavBytes = new Uint8Array(44 + pcmData.length);
  wavBytes.set(new Uint8Array(header), 0);
  wavBytes.set(pcmData, 44);
  return wavBytes.buffer;
}

/**
 * Detects whether a system speech synthesis voice is inherently female.
 */
export function isVoiceFemale(voiceName: string): boolean {
  const lower = voiceName.toLowerCase();
  return (
    lower.includes('zira') ||
    lower.includes('jenny') ||
    lower.includes('samantha') ||
    lower.includes('victoria') ||
    lower.includes('hazel') ||
    lower.includes('susan') ||
    lower.includes('female') ||
    lower.includes('woman') ||
    lower.includes('girl') ||
    lower.includes('karen') ||
    lower.includes('aria') ||
    lower.includes('catherine') ||
    lower.includes('linda') ||
    lower.includes('heera')
  );
}

/**
 * Detects whether a system speech synthesis voice is inherently male.
 */
export function isVoiceMale(voiceName: string): boolean {
  const lower = voiceName.toLowerCase();
  return (
    lower.includes('david') ||
    lower.includes('mark') ||
    lower.includes('guy') ||
    lower.includes('george') ||
    lower.includes('male') ||
    lower.includes('man') ||
    lower.includes('boy') ||
    lower.includes('roger') ||
    lower.includes('richard') ||
    lower.includes('sean') ||
    lower.includes('stefan') ||
    lower.includes('ravi')
  );
}

/**
 * Resolves a gender-guaranteed browser speech synthesis voice for a character preset.
 * Guarantees male characters NEVER use a female voice.
 */
export function resolveBrowserVoiceForCharacter(
  preset: CharacterVoicePreset,
  availableVoices: SpeechSynthesisVoice[],
  preferredVoiceName?: string
): SpeechSynthesisVoice | null {
  if (!availableVoices || availableVoices.length === 0) return null;

  // 1. If user explicitly picked a preferred voice for this session and it does not violate gender boundaries
  if (preferredVoiceName) {
    const userVoice = availableVoices.find((v) => v.name === preferredVoiceName);
    if (userVoice) {
      if (preset.gender === 'male' && !isVoiceFemale(userVoice.name)) {
        return userVoice;
      }
      if (preset.gender === 'female' && !isVoiceMale(userVoice.name)) {
        return userVoice;
      }
      if (preset.gender === 'entity') {
        return userVoice;
      }
    }
  }

  const englishVoices = availableVoices.filter((v) => v.lang.startsWith('en'));
  const candidateVoices = englishVoices.length > 0 ? englishVoices : availableVoices;

  // 2. Search by character's priority patterns
  for (const pattern of preset.voiceSearchPatterns) {
    const match = candidateVoices.find((v) => {
      if (!v.name.toLowerCase().includes(pattern.toLowerCase())) return false;
      if (preset.gender === 'male' && isVoiceFemale(v.name)) return false;
      if (preset.gender === 'female' && isVoiceMale(v.name)) return false;
      return true;
    });
    if (match) return match;
  }

  // 3. Fallback matching strictly by gender
  if (preset.gender === 'male') {
    const maleVoice = candidateVoices.find(
      (v) => isVoiceMale(v.name) || (!isVoiceFemale(v.name) && !v.name.toLowerCase().includes('female'))
    );
    if (maleVoice) return maleVoice;
  } else if (preset.gender === 'female') {
    const femaleVoice = candidateVoices.find(
      (v) => isVoiceFemale(v.name) || v.name.toLowerCase().includes('female')
    );
    if (femaleVoice) return femaleVoice;
  }

  return candidateVoices[0] || availableVoices[0] || null;
}

/**
 * Live Voice Narration Controller
 * Seamlessly interfaces with Google Gemini 2.0 Flash Audio, Google Cloud TTS,
 * Antigravity Gemini in-character script synthesis, and high-fidelity local Web Audio DSP speech.
 */
export class NarrationController {
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private activeSourceNode: AudioBufferSourceNode | null = null;
  private isSpeaking = false;
  private isPaused = false;
  private isContinuous = false;
  private currentSpeakingPage: number | null = null;
  private currentSpeakingParagraphIndex: number | null = null;
  private currentSpeakingWordIndex: number | null = null;
  private onStateChangeListeners: Set<
    (
      speaking: boolean,
      paused: boolean,
      currentText?: string,
      pageNumber?: number,
      paragraphIndex?: number,
      wordIndex?: number
    ) => void
  > = new Set();

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.onvoiceschanged = () => {
        // Voice registry initialized
      };
    }
  }

  public subscribe(
    listener: (
      speaking: boolean,
      paused: boolean,
      currentText?: string,
      pageNumber?: number,
      paragraphIndex?: number,
      wordIndex?: number
    ) => void
  ): () => void {
    this.onStateChangeListeners.add(listener);
    listener(
      this.isSpeaking,
      this.isPaused,
      undefined,
      this.currentSpeakingPage ?? undefined,
      this.currentSpeakingParagraphIndex ?? undefined,
      this.currentSpeakingWordIndex ?? undefined
    );
    return () => this.onStateChangeListeners.delete(listener);
  }

  private notify(currentText?: string, paraIndex?: number, wordIndex?: number): void {
    if (paraIndex !== undefined) {
      this.currentSpeakingParagraphIndex = paraIndex;
    }
    if (wordIndex !== undefined) {
      this.currentSpeakingWordIndex = wordIndex;
    }
    for (const l of this.onStateChangeListeners) {
      l(
        this.isSpeaking,
        this.isPaused,
        currentText,
        this.currentSpeakingPage ?? undefined,
        this.currentSpeakingParagraphIndex ?? undefined,
        this.currentSpeakingWordIndex ?? undefined
      );
    }
  }

  public getSpeakingPage(): number | null {
    return this.currentSpeakingPage;
  }

  public getSpeakingParagraphIndex(): number | null {
    return this.currentSpeakingParagraphIndex;
  }

  public getSpeakingWordIndex(): number | null {
    return this.currentSpeakingWordIndex;
  }

  public isContinuousMode(): boolean {
    return this.isContinuous;
  }

  /**
   * Stops any ongoing playback immediately and clears active audio nodes and highlights.
   */
  public stop(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    if (this.activeSourceNode) {
      try {
        this.activeSourceNode.stop();
        this.activeSourceNode.disconnect();
      } catch {
        // ignore already stopped source node
      }
    }

    this.isSpeaking = false;
    this.isPaused = false;
    this.isContinuous = false;
    this.currentSpeakingPage = null;
    this.currentSpeakingParagraphIndex = null;
    this.currentSpeakingWordIndex = null;
    this.activeSourceNode = null;
    this.notify();
  }

  /**
   * Starts continuous, seamless narration beginning at startPageNum and advancing
   * page-by-page throughout the entire novel without stopping.
   */
  public async narrateContinuously(
    startPageNum: number,
    config: GoogleGenAIConfig = loadAIConfig()
  ): Promise<void> {
    return this.narrateFromParagraph(startPageNum, 0, config, undefined, true, 0);
  }

  /**
   * Starts seamless narration directly at a specific page and paragraph index (and optional word offset),
   * speaking immediately in the voice of that dialogue's owner (or elder narrator for prose),
   * and advancing continuously page-by-page throughout the entire novel.
   */
  public async narrateFromParagraph(
    pageNum: number,
    startParaIndex: number,
    config: GoogleGenAIConfig = loadAIConfig(),
    overridePresetId?: string,
    isContinuous: boolean = true,
    startWordIndex?: number
  ): Promise<void> {
    this.stop();
    this.isContinuous = isContinuous;
    this.isSpeaking = true;
    this.isPaused = false;
    this.currentSpeakingPage = pageNum;
    this.currentSpeakingParagraphIndex = startParaIndex;

    let curPage = pageNum;
    let isFirstPage = true;

    while (this.isSpeaking && curPage <= TOTAL_PAGES) {
      const pageData = getPage(curPage);
      if (!pageData) break;

      const isLastPage = curPage >= TOTAL_PAGES;
      const startIndex = isFirstPage ? startParaIndex : 0;
      const wordIndex = isFirstPage ? startWordIndex : undefined;

      const finished = await this.narratePageStream(
        pageData.text,
        config,
        overridePresetId,
        curPage,
        !this.isContinuous || isLastPage,
        startIndex,
        wordIndex
      );

      isFirstPage = false;

      if (!finished || !this.isSpeaking || !this.isContinuous) {
        break;
      }

      if (!isLastPage) {
        curPage++;
        this.currentSpeakingPage = curPage;
        await new Promise((r) => setTimeout(r, 260));
      } else {
        break;
      }
    }

    this.isSpeaking = false;
    this.isPaused = false;
    this.isContinuous = false;
    this.currentSpeakingPage = null;
    this.currentSpeakingParagraphIndex = null;
    this.activeSourceNode = null;
    this.notify();
  }

  /**
   * Narrates the provided paragraphs with bone-chilling, atmospheric pacing.
   * Dynamically differentiates characters (Marcus, Elena, Silas, Peter, Thorne)
   * with gender-guaranteed voices, procedural radio comms squelch, and pitch calibration.
   */
  public async narratePage(
    paragraphs: string[],
    config: GoogleGenAIConfig = loadAIConfig(),
    overridePresetId?: string,
    pageNum?: number
  ): Promise<void> {
    this.stop();
    this.isContinuous = false;
    if (pageNum !== undefined) {
      this.currentSpeakingPage = pageNum;
    }
    await this.narratePageStream(paragraphs, config, overridePresetId, pageNum, true);
  }

  public async narratePageStream(
    paragraphs: string[],
    config: GoogleGenAIConfig = loadAIConfig(),
    overridePresetId?: string,
    pageNum?: number,
    isEndOfStream: boolean = true,
    startParaIndex: number = 0,
    startWordIndex?: number
  ): Promise<boolean> {
    if (!paragraphs || paragraphs.length === 0) return true;
    if (pageNum !== undefined) {
      this.currentSpeakingPage = pageNum;
    }

    const fullText = paragraphs.join(' ... ');
    const activeFirstParagraph = paragraphs[startParaIndex] || paragraphs[0];

    // Track 1: Google Gemini 2.0 Flash Cloud Audio Narration (via API key or OAuth Bearer)
    if (
      (config.apiKey || config.oauthToken) &&
      config.voiceModel === 'gemini-2.0-flash'
    ) {
      try {
        const audioBufferData = await this.synthesizeWithGeminiAPI(
          fullText,
          config.apiKey,
          config.oauthToken,
          config.geminiVoice,
          overridePresetId || config.characterPreset
        );
        if (audioBufferData) {
          await this.playAudioBuffer(audioBufferData, config, activeFirstParagraph);
          return true;
        }
      } catch (err) {
        console.warn(
          '[Narration] Gemini 2.0 Cloud Audio synthesis failed, trying alternative tracks:',
          err
        );
      }
    }

    // Track 2: Authentic Neural Generative & ElevenLabs Dramatic Engine (Local / Cloud Azure Models via edge-tts or ElevenLabs)
    // Guarantees 0% robotic voice, 100% distinct human male & female character personas
    if (
      config.voiceModel === 'neural-generative' ||
      config.voiceModel === 'elevenlabs' ||
      (!config.apiKey && !config.oauthToken && config.voiceModel !== 'antigravity-gemini' && config.voiceModel !== 'web-speech-dsp')
    ) {
      try {
        const streamed = await this.synthesizeWithNeuralStream(
          paragraphs,
          config,
          overridePresetId,
          pageNum,
          isEndOfStream,
          startParaIndex,
          startWordIndex
        );
        if (streamed) {
          return true;
        }
      } catch (err) {
        console.warn('[Narration] Neural Generative synthesis stream failed, trying fallback tracks:', err);
      }
    }

    // Track 3: Google Cloud Text-to-Speech API (Journey-D / Studio-M)
    if (
      (config.apiKey || config.oauthToken) &&
      config.voiceModel === 'google-cloud-tts'
    ) {
      try {
        const audioBufferData = await this.synthesizeWithGoogleCloudTTS(
          fullText,
          config.apiKey,
          config.oauthToken
        );
        if (audioBufferData) {
          await this.playAudioBuffer(audioBufferData, config, activeFirstParagraph);
          return true;
        }
      } catch (err) {
        console.warn(
          '[Narration] Google Cloud TTS synthesis failed, trying alternative tracks:',
          err
        );
      }
    }

    // Track 4: Antigravity / Universal Bridge Gemini 3.8 Flash In-Character Scripting + Web Audio DSP
    if (config.voiceModel === 'antigravity-gemini') {
      try {
        const expandedScript = await this.synthesizeScriptWithAntigravityGemini(
          activeFirstParagraph,
          config
        );
        if (expandedScript) {
          await this.synthesizeMultiSpeakerWebSpeech([expandedScript], config, overridePresetId, pageNum, isEndOfStream, 0, startWordIndex);
          return true;
        }
      } catch (err) {
        console.warn(
          '[Narration] Antigravity Gemini script synthesis failed, falling back to multi-speaker speech:',
          err
        );
      }
    }

    // Track 5: High-Fidelity Local Atmospheric Multi-Speaker Web Speech DSP Engine
    await this.synthesizeMultiSpeakerWebSpeech(paragraphs, config, overridePresetId, pageNum, isEndOfStream, startParaIndex, startWordIndex);
    return true;
  }

  /**
   * Generates a spoken in-character monologue from Marcus Ward using Gemini 3.8 Flash.
   */
  private async synthesizeScriptWithAntigravityGemini(
    sceneExcerpt: string,
    config: GoogleGenAIConfig
  ): Promise<string | null> {
    const baseUrl = config.proxyUrl || 'http://127.0.0.1:8045/v1';
    const endpoint = `${baseUrl.replace(/\/+$/, '')}/chat/completions`;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (config.providerMode === 'antigravity-tools' && config.proxyApiKey) {
      headers['Authorization'] = `Bearer ${config.proxyApiKey}`;
    } else if (config.providerMode === 'google-oauth' && config.oauthToken) {
      headers['Authorization'] = `Bearer ${config.oauthToken}`;
    }

    const res = await fetch(endpoint, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        model: config.chatModel || 'gemini-3.8-flash',
        messages: [
          {
            role: 'system',
            content:
              'You are Marcus Ward, 54-year-old jump engineer aboard USSC Geryon. Turn the passage into a 2-sentence quiet, gravelly, bone-chilling spoken voiceover monologue for space helmet comms.',
          },
          {
            role: 'user',
            content: `Passage: "${sceneExcerpt.slice(0, 500)}"`,
          },
        ],
        max_tokens: 120,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      return data.choices?.[0]?.message?.content?.trim() || null;
    }
    return null;
  }

  /**
   * Synthesizes audio using Google Gemini 2.0 Flash native multimodal audio API.
   */
  private async synthesizeWithGeminiAPI(
    text: string,
    apiKey: string,
    oauthToken: string,
    voice: GoogleGeminiVoice = 'Fenrir',
    characterId?: string
  ): Promise<ArrayBuffer | null> {
    const url = apiKey
      ? `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`
      : `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent`;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!apiKey && oauthToken) {
      headers['Authorization'] = `Bearer ${oauthToken}`;
    }

    const dossier = characterId ? CHARACTER_ACOUSTIC_DOSSIERS[characterId] : undefined;
    const resolvedVoice = (dossier?.engineMappings?.gemini2FlashVoice as GoogleGeminiVoice) || voice;
    const actingPrompt = dossier?.engineMappings?.geminiActingPrompt ||
      'You are Marcus Ward, a weary 54-year-old jump diagnostics engineer aboard the heavy hauler USSC Geryon. Read the following sci-fi excerpt with gravelly, measured, quiet atmospheric dread, speaking across an internal helmet radio in space:';

    const promptText = `${actingPrompt}\n\n"${text.slice(0, 750)}"`;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        contents: [
          {
            parts: [{ text: promptText }],
          },
        ],
        generationConfig: {
          responseModalities: ['AUDIO'],
          speechConfig: {
            voiceConfig: {
              prebuiltVoiceConfig: {
                voiceName: resolvedVoice,
              },
            },
          },
        },
      }),
    });

    if (!response.ok) {
      const errDetail = await response.text();
      throw new Error(`Gemini Audio API error (${response.status}): ${errDetail}`);
    }

    const data = await response.json();
    const candidatePart = data.candidates?.[0]?.content?.parts?.[0];
    const inlineData = candidatePart?.inlineData;

    if (inlineData?.data) {
      const rawBytes = base64ToUint8Array(inlineData.data);
      const mime = (inlineData.mimeType || '').toLowerCase();

      // If raw PCM, add standard 44-byte WAV header
      if (mime.includes('pcm')) {
        const rateMatch = mime.match(/rate=(\d+)/);
        const sampleRate = rateMatch ? parseInt(rateMatch[1], 10) : 24000;
        return pcmToWav(rawBytes, sampleRate, 1);
      }

      return rawBytes.buffer;
    }

    return null;
  }

  /**
   * Synthesizes audio using Google Cloud Text-to-Speech API.
   */
  private async synthesizeWithGoogleCloudTTS(
    text: string,
    apiKey: string,
    oauthToken: string
  ): Promise<ArrayBuffer | null> {
    const url = apiKey
      ? `https://texttospeech.googleapis.com/v1/text:synthesize?key=${apiKey}`
      : `https://texttospeech.googleapis.com/v1/text:synthesize`;

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (!apiKey && oauthToken) {
      headers['Authorization'] = `Bearer ${oauthToken}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        input: { text: text.slice(0, 950) },
        voice: {
          languageCode: 'en-US',
          name: 'en-US-Journey-D',
        },
        audioConfig: {
          audioEncoding: 'MP3',
          speakingRate: 0.88,
          pitch: -2.0,
        },
      }),
    });

    if (!response.ok) {
      const errText = await response.text();
      throw new Error(`Google Cloud TTS error (${response.status}): ${errText}`);
    }

    const data = await response.json();
    if (data.audioContent) {
      const bytes = base64ToUint8Array(data.audioContent);
      return bytes.buffer;
    }

    return null;
  }

  /**
   * Decodes and plays raw audio buffer through the Web Audio graph with atmospheric filtering.
   */
  private async playAudioBuffer(
    bufferData: ArrayBuffer,
    config: GoogleGenAIConfig,
    activeText: string
  ): Promise<void> {
    const audioEngine = getAudioEngine();
    const ctx = audioEngine.getAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const decodedBuffer = await ctx.decodeAudioData(bufferData.slice(0));
    const sourceNode = ctx.createBufferSource();
    sourceNode.buffer = decodedBuffer;
    sourceNode.playbackRate.value = config.narratorPace;

    const masterBus = audioEngine.getMasterBus() || ctx.destination;

    if (config.atmosphericFilter) {
      // EVA helmet communications filter: bandpass + presence shelf
      const bandpass = ctx.createBiquadFilter();
      bandpass.type = 'bandpass';
      bandpass.frequency.value = 1400;
      bandpass.Q.value = 0.85;

      const voiceGain = ctx.createGain();
      voiceGain.gain.value = 1.4;

      sourceNode.connect(bandpass);
      bandpass.connect(voiceGain);
      voiceGain.connect(masterBus);
    } else {
      sourceNode.connect(masterBus);
    }

    this.activeSourceNode = sourceNode;
    this.isSpeaking = true;
    this.isPaused = false;
    this.notify(activeText);

    sourceNode.onended = () => {
      this.isSpeaking = false;
      this.isPaused = false;
      this.activeSourceNode = null;
      this.notify();
    };

    sourceNode.start(0);
  }

  /**
   * Synthesizes audio using Google's authentic neural speech stream.
   * Fetches real MP3 audio bytes through the /api/tts proxy, decodes via Web Audio,
   * applies the USSC Geryon EVA helmet comms filter, and plays sequentially.
   */
  public async synthesizeWithGoogleStream(
    paragraphs: string[],
    config: GoogleGenAIConfig
  ): Promise<boolean> {
    const audioEngine = getAudioEngine();
    const ctx = audioEngine.getAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    // Clean text and split into sentences
    const fullText = paragraphs
      .map((p) => p.replace(/^ACT\s+[IVXLCDM]+:[^—–-]+[—–-]\s*/i, '').trim())
      .join(' ... ');
    const rawSentences = fullText.split(/(?<=[.?!…])\s+/);
    const chunks: string[] = [];
    let currentChunk = '';

    for (const s of rawSentences) {
      if (!s.trim()) continue;
      if ((currentChunk + ' ' + s).length > 180) {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = s;
      } else {
        currentChunk = currentChunk ? `${currentChunk} ${s}` : s;
      }
    }
    if (currentChunk && currentChunk.trim()) chunks.push(currentChunk.trim());

    if (chunks.length === 0) return false;

    this.isSpeaking = true;
    this.isPaused = false;
    this.notify(chunks[0]);

    for (let i = 0; i < chunks.length; i++) {
      if (!this.isSpeaking) break;

      const chunkText = chunks[i];
      const audioBuffer = await this.fetchGoogleTTSBuffer(chunkText, ctx);
      if (audioBuffer && this.isSpeaking) {
        await this.playSingleBuffer(audioBuffer, config, chunkText);
      }
    }

    this.isSpeaking = false;
    this.isPaused = false;
    this.activeSourceNode = null;
    this.notify();
    return true;
  }

  /**
   * Synthesizes audio using the Neural Generative Speech Engine (edge-tts / Azure neural models).
   * Automatically differentiates characters: Christopher (Marcus), Aria (Elena), Guy (Thorne),
   * Brian (Silas), Eric (Peter), Andrew (Margin Spectator).
   * 100% authentic neural voices, 0% robotic desktop TTS.
   */
  public async synthesizeWithNeuralStream(
    paragraphs: string[],
    config: GoogleGenAIConfig,
    overridePresetId?: string,
    pageNum?: number,
    isEndOfStream: boolean = true,
    startParaIndex: number = 0,
    startWordIndex?: number
  ): Promise<boolean> {
    const audioEngine = getAudioEngine();
    const ctx = audioEngine.getAudioContext();
    if (ctx.state === 'suspended') {
      await ctx.resume();
    }

    const formatted = formatPageParagraphs(paragraphs);
    if (formatted.length === 0) return false;

    this.isSpeaking = true;
    this.isPaused = false;
    if (pageNum !== undefined) this.currentSpeakingPage = pageNum;

    for (let i = startParaIndex; i < formatted.length; i++) {
      if (!this.isSpeaking) break;

      const item = formatted[i];
      if (!item.text.trim()) continue;

      // Filter out technical marginalia / medical logs if marginaliaReadMode is 'skip' (default)
      if (item.type === 'marginalia' && config.marginaliaReadMode !== 'auto') {
        continue;
      }

      let spokenText = item.text;
      if (i === startParaIndex && startWordIndex && startWordIndex > 0) {
        const words = spokenText.split(/\s+/);
        if (startWordIndex < words.length) {
          spokenText = words.slice(startWordIndex).join(' ');
        }
      }

      let preset: CharacterVoicePreset;
      if (overridePresetId && CHARACTER_VOICE_PRESETS[overridePresetId]) {
        preset = CHARACTER_VOICE_PRESETS[overridePresetId];
      } else if (config.multiVoiceNarration !== false && item.characterId && CHARACTER_VOICE_PRESETS[item.characterId]) {
        preset = CHARACTER_VOICE_PRESETS[item.characterId];
      } else {
        const activeKey = config.characterPreset;
        preset = (activeKey && CHARACTER_VOICE_PRESETS[activeKey]) || CHARACTER_VOICE_PRESETS.narrator;
      }

      this.currentSpeakingParagraphIndex = i;
      this.notify(spokenText, i);
      const audioBuffer = await this.fetchNeuralTTSBuffer(
        spokenText,
        preset,
        config,
        ctx,
        item.emotionCategory || item.emotion
      );
      if (audioBuffer && this.isSpeaking) {
        const startOffset = i === startParaIndex ? (startWordIndex || 0) : 0;
        await this.playSingleBuffer(audioBuffer, config, spokenText, preset, startOffset);
      }
    }

    if (isEndOfStream) {
      this.isSpeaking = false;
      this.isPaused = false;
      this.activeSourceNode = null;
      this.notify();
    }
    return true;
  }

  private async fetchNeuralTTSBuffer(
    text: string,
    preset: CharacterVoicePreset,
    config: GoogleGenAIConfig,
    ctx: AudioContext,
    emotion?: string
  ): Promise<AudioBuffer | null> {
    const cleanText = text.replace(/^ACT\s+[IVXLCDM]+:[^—–-]+[—–-]\s*/i, '').trim();
    if (!cleanText) return null;

    // Track 0: ElevenLabs High-Fidelity Neural Actor Engine (Cloud API or Local Performance Samples)
    if (config.voiceModel === 'elevenlabs') {
      const elevenBuf = await this.fetchElevenLabsTTSBuffer(cleanText, preset, config, ctx, emotion);
      if (elevenBuf) {
        return elevenBuf;
      }
    }

    const voice = preset.id || preset.neuralVoice || 'en-US-ChristopherNeural';
    const isCustomized = (config.characterPreset || 'marcus_ward') === preset.id;
    const paceVal = (isCustomized ? config.voicePace : preset.voicePace) ?? 1.0;
    const ratePercent = Math.round((paceVal - 1.0) * 100);
    const rateStr = ratePercent >= 0 ? `+${ratePercent}%` : `${ratePercent}%`;

    const pitchVal = (isCustomized ? config.voicePitch : preset.voicePitch) ?? 1.0;
    const pitchOffsetHz = Math.round((pitchVal - 1.0) * 25);
    const pitchStr = pitchOffsetHz >= 0 ? `+${pitchOffsetHz}Hz` : `${pitchOffsetHz}Hz`;

    const encodedText = encodeURIComponent(cleanText);
    const emotionQuery = emotion ? `&emotion=${encodeURIComponent(emotion)}` : '';
    const url = `/api/neural-tts?voice=${encodeURIComponent(voice)}&text=${encodedText}&rate=${encodeURIComponent(rateStr)}&pitch=${encodeURIComponent(pitchStr)}${emotionQuery}`;

    try {
      const res = await fetch(url);
      if (res.ok) {
        const arrayBuf = await res.arrayBuffer();
        if (arrayBuf.byteLength > 100) {
          return await ctx.decodeAudioData(arrayBuf);
        }
      }
    } catch {
      // Direct daemon fallback on port 5175 if Vite proxy is busy
      try {
        const directUrl = `http://127.0.0.1:5175/synthesize?voice=${encodeURIComponent(voice)}&text=${encodedText}&rate=${encodeURIComponent(rateStr)}&pitch=${encodeURIComponent(pitchStr)}${emotionQuery}`;
        const res = await fetch(directUrl);
        if (res.ok) {
          const arrayBuf = await res.arrayBuffer();
          if (arrayBuf.byteLength > 100) {
            return await ctx.decodeAudioData(arrayBuf);
          }
        }
      } catch {
        // Fallback handled downstream
      }
    }

    return null;
  }

  /**
   * Synthesizes audio using ElevenLabs Neural Speech API (eleven_multilingual_v2 / eleven_turbo_v2_5).
   * Direct high-fidelity neural acting with breath control, emotional stability, and style exaggeration.
   * Seamlessly checks for pre-rendered local performance samples before calling the cloud API.
   */
  private async fetchElevenLabsTTSBuffer(
    text: string,
    preset: CharacterVoicePreset,
    config: GoogleGenAIConfig,
    ctx: AudioContext,
    emotion?: string
  ): Promise<AudioBuffer | null> {
    const cleanText = text.replace(/^ACT\s+[IVXLCDM]+:[^—–-]+[—–-]\s*/i, '').trim();
    if (!cleanText) return null;

    const charId = preset.id || 'marcus_ward';
    const states = ELEVENLABS_CHARACTER_STATES[charId] || ELEVENLABS_CHARACTER_STATES['marcus_ward'] || [];
    const activeState =
      (config.characterStateId && states.find((s) => s.stateId === config.characterStateId)) ||
      (emotion &&
        states.find(
          (s) =>
            s.stateName.toLowerCase().includes(emotion.toLowerCase()) ||
            s.stateId.toLowerCase().includes(emotion.toLowerCase())
        )) ||
      states[0];

    // 1. Check if a pre-generated performance sample exists locally in public/audio/samples/elevenlabs/
    if (activeState?.targetFile) {
      try {
        const localPath = `/audio/samples/elevenlabs/${activeState.targetFile}`;
        const checkRes = await fetch(localPath, { method: 'HEAD' });
        if (checkRes.ok) {
          const sampleRes = await fetch(localPath);
          if (sampleRes.ok) {
            const arrayBuf = await sampleRes.arrayBuffer();
            if (arrayBuf.byteLength > 100) {
              return await ctx.decodeAudioData(arrayBuf);
            }
          }
        }
      } catch {
        // Fall through to ElevenLabs Cloud API
      }
    }

    const apiKey =
      config.elevenLabsApiKey ||
      (typeof import.meta !== 'undefined' &&
        ((import.meta as any).env?.VITE_ELEVENLABS_API_KEY || (import.meta as any).env?.ELEVENLABS_API_KEY)) ||
      '';

    if (!apiKey) {
      return null;
    }

    const voiceId = activeState?.voiceId || 'pNInz6obpgDQGcFmaJgB';
    const modelId = config.elevenLabsModel || 'eleven_multilingual_v2';

    try {
      const url = `https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Accept: 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey,
        },
        body: JSON.stringify({
          text: cleanText,
          model_id: modelId,
          voice_settings: {
            stability: activeState?.stability ?? 0.65,
            similarity_boost: activeState?.similarity ?? 0.80,
            style: activeState?.style ?? 0.25,
            use_speaker_boost: activeState?.boost ?? true,
          },
        }),
      });

      if (res.ok) {
        const arrayBuf = await res.arrayBuffer();
        if (arrayBuf.byteLength > 100) {
          return await ctx.decodeAudioData(arrayBuf);
        }
      } else {
        const errText = await res.text();
        console.warn(`[ElevenLabs] API synthesis error ${res.status}:`, errText);
      }
    } catch (err) {
      console.warn('[ElevenLabs] Cloud synthesis request failed:', err);
    }

    return null;
  }

  private async fetchGoogleTTSBuffer(text: string, ctx: AudioContext): Promise<AudioBuffer | null> {
    const encoded = encodeURIComponent(text.slice(0, 190));
    const url = `/api/tts?ie=UTF-8&q=${encoded}&tl=en&client=tw-ob`;

    try {
      const res = await fetch(url);
      if (res.ok) {
        const arrayBuf = await res.arrayBuffer();
        if (arrayBuf.byteLength > 100) {
          return await ctx.decodeAudioData(arrayBuf);
        }
      }
    } catch {
      // Direct fallback
    }

    return null;
  }

  /**
   * Generates a procedural radio mic squelch / PTT click impulse (pure Web Audio, 0 audio files)
   */
  private playCommsSquelch(ctx: AudioContext, destination: AudioNode, isIntro: boolean): void {
    try {
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();

      filter.type = 'bandpass';
      filter.frequency.setValueAtTime(isIntro ? 2600 : 1900, t);
      filter.Q.setValueAtTime(3.5, t);

      osc.type = 'sine';
      if (isIntro) {
        osc.frequency.setValueAtTime(1600, t);
        osc.frequency.exponentialRampToValueAtTime(420, t + 0.032);
      } else {
        osc.frequency.setValueAtTime(750, t);
        osc.frequency.exponentialRampToValueAtTime(220, t + 0.045);
      }

      gain.gain.setValueAtTime(0.001, t);
      gain.gain.linearRampToValueAtTime(0.22, t + 0.008);
      gain.gain.exponentialRampToValueAtTime(0.001, t + (isIntro ? 0.038 : 0.052));

      osc.connect(filter);
      filter.connect(gain);
      gain.connect(destination);

      osc.start(t);
      osc.stop(t + (isIntro ? 0.042 : 0.058));
    } catch {
      // Audio node allocation safeguard
    }
  }

  /**
   * Creates a non-linear transfer curve for authentic analog comms tube saturation
   */
  private makeDistortionCurve(amount: number): Float32Array {
    const k = Math.max(1, amount);
    const n_samples = 22050;
    const curve = new Float32Array(n_samples);
    const deg = Math.PI / 180;
    for (let i = 0; i < n_samples; ++i) {
      const x = (i * 2) / n_samples - 1;
      curve[i] = ((3 + k) * x * 20 * deg) / (Math.PI + k * Math.abs(x));
    }
    return curve;
  }

  private playSingleBuffer(
    buffer: AudioBuffer,
    config: GoogleGenAIConfig,
    text: string,
    preset?: CharacterVoicePreset,
    wordOffset: number = 0
  ): Promise<void> {
    return new Promise((resolve) => {
      const audioEngine = getAudioEngine();
      const ctx = audioEngine.getAudioContext();
      if (ctx.state === 'suspended') {
        ctx.resume();
      }

      const masterBus = audioEngine.getMasterBus() || ctx.destination;

      const isCustomized = preset && (config.characterPreset || 'marcus_ward') === preset.id;
      const isCommLink = config.masteringProfile === 'comm_link';
      const squelchClicks = isCommLink
        ? (isCustomized ? (config.squelchClicks ?? true) : (preset?.squelchClicks ?? true))
        : (config.squelchClicks ?? false);
      const chestResonance = isCustomized ? (config.chestResonance ?? 0.85) : (preset?.chestResonance ?? config.chestResonance ?? 0.85);
      const commsGrit = isCustomized ? (config.commsGrit ?? 0.65) : (preset?.commsGrit ?? config.commsGrit ?? 0.65);
      const cabinReverb = isCustomized ? (config.cabinReverb ?? 0.25) : (preset?.cabinReverb ?? config.cabinReverb ?? 0.25);

      // 1. Procedural Comms Squelch Mic Click at start of transmission (if enabled)
      if (squelchClicks) {
        this.playCommsSquelch(ctx, masterBus, true);
      }

      const source = ctx.createBufferSource();
      source.buffer = buffer;

      // 2. Playback speed & pitch
      // Neural buffers from edge-tts/Azure and ElevenLabs are already natively synthesized with the exact requested
      // pitch & speech rate. DO NOT double-resample or modify playbackRate.
      if (config.voiceModel === 'neural-generative' || config.voiceModel === 'elevenlabs') {
        source.playbackRate.value = 1.0;
      } else {
        const pitch = (isCustomized ? config.voicePitch : preset?.voicePitch) ?? config.narratorPitch ?? 0.80;
        const pace = (isCustomized ? config.voicePace : preset?.voicePace) ?? config.narratorPace ?? 0.86;
        source.playbackRate.value = Math.max(0.48, Math.min(1.85, pitch * pace));
      }

      // Master Vocal Bus with anti-click crossfade envelope
      const vocalBus = ctx.createGain();
      const startTime = ctx.currentTime;
      vocalBus.gain.setValueAtTime(0.001, startTime);
      vocalBus.gain.linearRampToValueAtTime(1.25, startTime + 0.012);

      if (isCommLink) {
        // --- MODE 2: EVA COMM-LINK TRANSCEIVER (Tactical Helmet Radio) ---
        const shaper = ctx.createWaveShaper();
        shaper.curve = this.makeDistortionCurve(Math.min(5, commsGrit * 5));
        shaper.oversample = '2x';

        const highpass = ctx.createBiquadFilter();
        highpass.type = 'highpass';
        highpass.frequency.value = 280;

        const lowpass = ctx.createBiquadFilter();
        lowpass.type = 'lowpass';
        lowpass.frequency.value = 4200;

        const commPresence = ctx.createBiquadFilter();
        commPresence.type = 'peaking';
        commPresence.frequency.value = 2200;
        commPresence.gain.value = 3.5;
        commPresence.Q.value = 1.0;

        source.connect(shaper);
        shaper.connect(highpass);
        highpass.connect(lowpass);
        lowpass.connect(commPresence);
        commPresence.connect(vocalBus);
      } else {
        // --- MODE 1: STUDIO MASTER (Default - Broadcast Fidelity) ---
        // A. Low-Shelf Warmth & Authority (130Hz)
        const lowShelf = ctx.createBiquadFilter();
        lowShelf.type = 'lowshelf';
        lowShelf.frequency.value = 130;
        lowShelf.gain.value = Math.min(4.0, Math.max(0.5, chestResonance * 2.8));

        // B. Vocal Intelligibility & Articulation Peaking EQ (3200Hz, Q=0.75)
        const presence = ctx.createBiquadFilter();
        presence.type = 'peaking';
        presence.frequency.value = 3200;
        presence.gain.value = 2.4;
        presence.Q.value = 0.75;

        // C. Air & Breath High-Shelf (9500Hz)
        const airShelf = ctx.createBiquadFilter();
        airShelf.type = 'highshelf';
        airShelf.frequency.value = 9500;
        airShelf.gain.value = 1.8;

        // D. Broadcast Dynamics Compressor (Studio Vocal Leveling)
        const comp = ctx.createDynamicsCompressor();
        comp.threshold.value = -18;
        comp.knee.value = 10;
        comp.ratio.value = 2.8;
        comp.attack.value = 0.012;
        comp.release.value = 0.12;

        source.connect(lowShelf);
        lowShelf.connect(presence);
        presence.connect(airShelf);
        airShelf.connect(comp);
        comp.connect(vocalBus);
      }

      // Starship Corridor / Chamber Ambience Delay
      if (cabinReverb > 0.04) {
        const delay = ctx.createDelay();
        delay.delayTime.value = 0.032; // 32ms subtle acoustic reflection
        const delayGain = ctx.createGain();
        delayGain.gain.value = Math.min(0.22, cabinReverb * 0.22);
        const delayFilter = ctx.createBiquadFilter();
        delayFilter.type = 'lowpass';
        delayFilter.frequency.value = 3200;

        vocalBus.connect(delay);
        delay.connect(delayFilter);
        delayFilter.connect(delayGain);
        delayGain.connect(masterBus);
      }

      vocalBus.connect(masterBus);

      // --- REAL-TIME PER-WORD TIMING & HIGHLIGHT TRACKING ---
      const rawWords = text.split(/\s+/).filter(Boolean);
      const wordWeights = rawWords.map((w) => {
        const clean = w.replace(/[^\w]/g, '');
        let len = Math.max(2, clean.length);
        if (/^(a|an|the|in|on|at|of|to|and|is|it|as|by|or|we|he|she|they|i)$/i.test(clean)) {
          len *= 0.70;
        } else if (len >= 8) {
          len *= 1.20;
        }
        let pauseSec = 0;
        if (/[,:;—–]/.test(w)) pauseSec = 0.16;
        else if (/[.!?…]/.test(w)) pauseSec = 0.35;
        return { word: w, len, pauseSec };
      });

      const totalLen = Math.max(1, wordWeights.reduce((acc, c) => acc + c.len, 0));
      const totalPauses = wordWeights.reduce((acc, c) => acc + c.pauseSec, 0);
      const effectiveDuration = Math.max(0.4, buffer.duration - totalPauses);

      let currentSec = 0;
      const wordIntervals: { word: string; startSec: number; endSec: number }[] = [];
      for (const item of wordWeights) {
        const dur = (item.len / totalLen) * effectiveDuration;
        const startSec = currentSec;
        const endSec = currentSec + dur + item.pauseSec;
        wordIntervals.push({ word: item.word, startSec, endSec });
        currentSec = endSec;
      }

      let activeWordIdx = -1;
      let timerIntervalId: any = null;

      const checkWordProgress = () => {
        if (!this.isSpeaking) return;
        const elapsed = ctx.currentTime - startTime;
        let found = -1;
        for (let idx = 0; idx < wordIntervals.length; idx++) {
          if (elapsed >= wordIntervals[idx].startSec && elapsed < wordIntervals[idx].endSec) {
            found = idx;
            break;
          }
        }
        if (found === -1 && elapsed >= (wordIntervals[wordIntervals.length - 1]?.endSec || 0)) {
          found = wordIntervals.length - 1;
        }

        if (found !== -1 && found !== activeWordIdx) {
          activeWordIdx = found;
          const absoluteWordIdx = wordOffset + found;
          this.currentSpeakingWordIndex = absoluteWordIdx;
          this.notify(text, this.currentSpeakingParagraphIndex ?? undefined, absoluteWordIdx);
        }
      };

      // Poll at high resolution (~30ms) for ultra-accurate word sync
      timerIntervalId = setInterval(checkWordProgress, 30);
      checkWordProgress();

      this.activeSourceNode = source;
      this.notify(text, this.currentSpeakingParagraphIndex ?? undefined, wordOffset);

      source.onended = () => {
        if (timerIntervalId) clearInterval(timerIntervalId);
        this.currentSpeakingWordIndex = null;
        if (squelchClicks) {
          this.playCommsSquelch(ctx, masterBus, false);
        }
        resolve();
      };

      source.start(0);
    });
  }

  /**
   * Multi-Speaker Speech Synthesis Engine
   * Sequentially speaks page paragraphs, automatically differentiating speaker personas:
   * - Marcus Ward: Weary, gravelly baritone male (pitch 0.74, pace 0.84)
   * - Elena Cruz: Analytical, sharp female (pitch 1.04, pace 0.98)
   * - Dr. Simon Peter: Psychiatric officer male tenor (pitch 0.90, pace 0.88)
   * - Cadet Silas Vance: Tense junior technician male (pitch 1.15, pace 1.04)
   * - Inquisitor Thorne: Menacing tribunal deep male bass (pitch 0.60, pace 0.78)
   */
  public async synthesizeMultiSpeakerWebSpeech(
    paragraphs: string[],
    config: GoogleGenAIConfig,
    overridePresetId?: string,
    pageNum?: number,
    isEndOfStream: boolean = true,
    startParaIndex: number = 0,
    startWordIndex?: number
  ): Promise<void> {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      console.warn('[Narration] Web Speech API not supported in this browser environment.');
      return;
    }

    const synth = window.speechSynthesis;
    synth.cancel();

    const formatted = formatPageParagraphs(paragraphs);
    if (formatted.length === 0) return;

    this.isSpeaking = true;
    this.isPaused = false;
    if (pageNum !== undefined) this.currentSpeakingPage = pageNum;

    const availableVoices = synth.getVoices();
    const audioEngine = getAudioEngine();
    const ctx = audioEngine.getAudioContext();

    for (let i = startParaIndex; i < formatted.length; i++) {
      if (!this.isSpeaking) break;

      const item = formatted[i];
      if (!item.text.trim()) continue;

      if (item.type === 'marginalia' && config.marginaliaReadMode !== 'auto') {
        continue;
      }

      let spokenText = item.text;
      if (i === startParaIndex && startWordIndex && startWordIndex > 0) {
        const words = spokenText.split(/\s+/);
        if (startWordIndex < words.length) {
          spokenText = words.slice(startWordIndex).join(' ');
        }
      }

      let preset: CharacterVoicePreset;
      if (overridePresetId && CHARACTER_VOICE_PRESETS[overridePresetId]) {
        preset = CHARACTER_VOICE_PRESETS[overridePresetId];
      } else if (config.multiVoiceNarration !== false && item.characterId && CHARACTER_VOICE_PRESETS[item.characterId]) {
        preset = CHARACTER_VOICE_PRESETS[item.characterId];
      } else {
        const activeKey = config.characterPreset;
        preset = (activeKey && CHARACTER_VOICE_PRESETS[activeKey]) || CHARACTER_VOICE_PRESETS.narrator;
      }

      // If preset matches currently calibrated preset in config, apply slider adjustments
      const isCustomized = (config.characterPreset || 'marcus_ward') === preset.id;
      const pitch = isCustomized ? config.voicePitch : preset.voicePitch;
      const pace = isCustomized ? config.voicePace : preset.voicePace;
      const squelch = isCustomized ? config.squelchClicks : preset.squelchClicks;

      const voice = resolveBrowserVoiceForCharacter(
        preset,
        availableVoices,
        config.preferredVoiceName
      );

      // Procedural walkie-talkie PTT intro click
      if (squelch && ctx && ctx.state !== 'closed') {
        const masterBus = audioEngine.getMasterBus() || ctx.destination;
        this.playCommsSquelch(ctx, masterBus, true);
      }

      this.currentSpeakingParagraphIndex = i;
      this.notify(spokenText, i);

      await new Promise<void>((resolve) => {
        const utterance = new SpeechSynthesisUtterance(spokenText);
        this.currentUtterance = utterance;

        if (voice) {
          utterance.voice = voice;
        }

        utterance.pitch = Math.max(0.45, Math.min(1.8, pitch));
        utterance.rate = Math.max(0.55, Math.min(1.6, pace));

        const words = spokenText.split(/\s+/).filter(Boolean);
        const wordOffset = i === startParaIndex ? (startWordIndex || 0) : 0;
        utterance.onboundary = (e: SpeechSynthesisEvent) => {
          if (e.name === 'word') {
            const charIdx = e.charIndex;
            let accum = 0;
            let foundWord = 0;
            for (let w = 0; w < words.length; w++) {
              accum += words[w].length + 1;
              if (accum > charIdx) {
                foundWord = w;
                break;
              }
            }
            const absWordIdx = wordOffset + foundWord;
            this.currentSpeakingWordIndex = absWordIdx;
            this.notify(spokenText, i, absWordIdx);
          }
        };

        utterance.onend = () => {
          this.currentSpeakingWordIndex = null;
          if (squelch && ctx && ctx.state !== 'closed') {
            const masterBus = audioEngine.getMasterBus() || ctx.destination;
            this.playCommsSquelch(ctx, masterBus, false);
          }
          this.currentUtterance = null;
          resolve();
        };

        utterance.onerror = (e) => {
          this.currentSpeakingWordIndex = null;
          console.warn('[Narration] Utterance error:', e);
          this.currentUtterance = null;
          resolve();
        };

        synth.speak(utterance);
      });

      // Brief conversational breathing pause between paragraphs
      if (this.isSpeaking && i < formatted.length - 1) {
        await new Promise((r) => setTimeout(r, 160));
      }
    }

    if (isEndOfStream) {
      this.isSpeaking = false;
      this.isPaused = false;
      this.currentUtterance = null;
      this.notify();
    }
  }

  /**
   * Local atmospheric voice synthesis fallback (legacy alias).
   */
  public synthesizeWithWebSpeech(paragraphs: string[], config: GoogleGenAIConfig): void {
    void this.synthesizeMultiSpeakerWebSpeech(paragraphs, config);
  }

  public pause(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.pause();
      this.isPaused = true;
      this.notify();
    }
  }

  public resume(): void {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.resume();
      this.isPaused = false;
      this.notify();
    }
  }

  public getState(): { isSpeaking: boolean; isPaused: boolean } {
    return { isSpeaking: this.isSpeaking, isPaused: this.isPaused };
  }

  public getCurrentUtterance(): SpeechSynthesisUtterance | null {
    return this.currentUtterance;
  }
}

let sharedNarrationController: NarrationController | null = null;
export function getNarrationController(): NarrationController {
  if (!sharedNarrationController) {
    sharedNarrationController = new NarrationController();
  }
  return sharedNarrationController;
}

/**
 * Live Google Imagen 3 / Antigravity Gemini Image Generation Service
 */
export async function generateArtworkWithGoogleAI(
  prompt: string,
  pageNum: number,
  config: GoogleGenAIConfig = loadAIConfig()
): Promise<string> {
  const enhancedPrompt = `Cinematic hyper-detailed dark sci-fi concept art, masterpiece: ${prompt}. Atmospheric cosmic dread, volumetric light beams, photorealistic textures, 8k resolution, cinematic composition, Syd Mead and Blade Runner aesthetic.`;

  // Route 1: Antigravity Tools / Local Proxy Gemini Image Model (gemini-3.1-flash-image)
  if (
    config.providerMode === 'antigravity-tools' ||
    config.imageModel === 'antigravity-gemini-image'
  ) {
    try {
      const baseUrl = config.proxyUrl || 'http://127.0.0.1:8045/v1';
      const endpoint = `${baseUrl.replace(/\/+$/, '')}/images/generations`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      if (config.proxyApiKey) {
        headers['Authorization'] = `Bearer ${config.proxyApiKey}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: enhancedPrompt,
          model: 'gemini-3.1-flash-image',
          n: 1,
          size: '1024x1024',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const b64 = data.data?.[0]?.b64_json;
        if (b64) {
          const fullDataUri = `data:image/jpeg;base64,${b64}`;
          saveGeneratedArtCache(pageNum, fullDataUri);
          return fullDataUri;
        }
      }
    } catch (err) {
      console.warn(
        '[GoogleGenAI] Antigravity Tools image generation failed, trying Cloud API:',
        err
      );
    }
  }

  // Route 2: Google Cloud Imagen 3 Endpoint (via API key or OAuth Token)
  if (config.apiKey || config.oauthToken) {
    try {
      const imagenUrl = config.apiKey
        ? `https://generativelanguage.googleapis.com/v1beta/models/imagen-3.0-generate-002:predict?key=${config.apiKey}`
        : `https://generativelanguage.googleapis.com/v1beta/models/imagen-3.0-generate-002:predict`;

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (!config.apiKey && config.oauthToken) {
        headers['Authorization'] = `Bearer ${config.oauthToken}`;
      }

      const response = await fetch(imagenUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          instances: [{ prompt: enhancedPrompt }],
          parameters: { sampleCount: 1, aspectRatio: '16:9' },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const b64 = data.predictions?.[0]?.bytesBase64Encoded;
        if (b64) {
          const fullDataUri = `data:image/jpeg;base64,${b64}`;
          saveGeneratedArtCache(pageNum, fullDataUri);
          return fullDataUri;
        }
      }
    } catch (err) {
      console.warn(
        '[GoogleGenAI] Imagen 3 Cloud API failed, falling back to procedural canvas:',
        err
      );
    }
  }

  // Route 3: Intelligent Procedural Sci-Fi Canvas Generator Fallback
  // Synthesizes a high-contrast dark sci-fi digital artwork in pure code
  const canvas = document.createElement('canvas');
  canvas.width = 960;
  canvas.height = 540;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Deep vacuum background
    const bgGrad = ctx.createRadialGradient(480, 270, 50, 480, 270, 540);
    bgGrad.addColorStop(0, '#0a1220');
    bgGrad.addColorStop(0.5, '#060a12');
    bgGrad.addColorStop(1, '#020306');
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, 960, 540);

    // Cyan/Violet Planck lattice lines
    ctx.strokeStyle = 'rgba(0, 243, 255, 0.15)';
    ctx.lineWidth = 1;
    for (let x = 0; x < 960; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, 540);
      ctx.stroke();
    }
    for (let y = 0; y < 540; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(960, y);
      ctx.stroke();
    }

    // Volumetric energy core / starship silhouette
    const coreGrad = ctx.createRadialGradient(480, 270, 10, 480, 270, 280);
    coreGrad.addColorStop(0, 'rgba(0, 243, 255, 0.7)');
    coreGrad.addColorStop(0.3, 'rgba(157, 78, 221, 0.4)');
    coreGrad.addColorStop(0.7, 'rgba(255, 42, 75, 0.15)');
    coreGrad.addColorStop(1, 'transparent');
    ctx.fillStyle = coreGrad;
    ctx.beginPath();
    ctx.arc(480, 270, 280, 0, Math.PI * 2);
    ctx.fill();

    // Geometric hull vectors
    ctx.strokeStyle = '#00f3ff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(180, 320);
    ctx.lineTo(480, 200);
    ctx.lineTo(780, 320);
    ctx.lineTo(480, 380);
    ctx.closePath();
    ctx.stroke();

    // Text telemetry stamp
    ctx.fillStyle = '#00f3ff';
    ctx.font = '12px monospace';
    ctx.fillText(`GOOGLE GEN-AI SYNTHESIS // SCENE ${pageNum} // CAESURA CORE`, 30, 500);
    ctx.fillStyle = '#6b7c96';
    ctx.fillText(`PROMPT: ${prompt.slice(0, 80)}...`, 30, 518);

    const generatedUri = canvas.toDataURL('image/png');
    saveGeneratedArtCache(pageNum, generatedUri);
    return generatedUri;
  }

  throw new Error('Canvas rendering context failed');
}

/**
 * Live In-Character Lore & Jump Diagnostics Analysis powered by Google Gemini 3.8 Flash.
 * Allows readers to consult Marcus Ward on any page for deep technical telemetry.
 */
export async function generateSceneCommentary(
  sceneText: string,
  pageNumber: number,
  config: GoogleGenAIConfig = loadAIConfig()
): Promise<string> {
  const cached = loadCommentaryCache();
  if (cached[pageNumber]) {
    return cached[pageNumber];
  }

  // 1. If using Antigravity Tools / Universal Model Bridge
  if (
    config.providerMode === 'antigravity-tools' ||
    config.providerMode === 'universal-bridge' ||
    !config.apiKey
  ) {
    try {
      const baseUrl =
        config.providerMode === 'universal-bridge'
          ? 'http://localhost:8056/v1'
          : config.proxyUrl || 'http://127.0.0.1:8045/v1';

      const endpoint = `${baseUrl.replace(/\/+$/, '')}/chat/completions`;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };

      if (config.providerMode === 'antigravity-tools' && config.proxyApiKey) {
        headers['Authorization'] = `Bearer ${config.proxyApiKey}`;
      } else if (config.providerMode === 'google-oauth' && config.oauthToken) {
        headers['Authorization'] = `Bearer ${config.oauthToken}`;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          model: config.chatModel || 'gemini-3.8-flash',
          messages: [
            {
              role: 'system',
              content:
                'You are Marcus Ward, 54-year-old jump diagnostics engineer aboard the USSC Geryon. Provide a 2 to 3 sentence gritty, dark sci-fi engineering analysis and existential log entry analyzing the current page of the Caesura jump logs. Speak in first person, referencing Planck coordinate tension, retinal grid bleed, or the unrendered margin.',
            },
            {
              role: 'user',
              content: `Page ${pageNumber} Jump Excerpt:\n"${sceneText.slice(0, 800)}"`,
            },
          ],
          max_tokens: 150,
          temperature: 0.7,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const content = data.choices?.[0]?.message?.content?.trim();
        if (content) {
          saveCommentaryCache(pageNumber, content);
          return content;
        }
      }
    } catch (err) {
      console.warn('[GoogleGenAI] Proxy chat completion failed, falling back:', err);
    }
  }

  // 2. Direct Gemini Generative Language API
  if (config.apiKey || config.oauthToken) {
    try {
      const url = config.apiKey
        ? `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${config.apiKey}`
        : `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent`;

      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (!config.apiKey && config.oauthToken) {
        headers['Authorization'] = `Bearer ${config.oauthToken}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          contents: [
            {
              parts: [
                {
                  text: `You are Marcus Ward, 54-year-old jump diagnostics engineer aboard the USSC Geryon. Provide a 2 to 3 sentence gritty engineering log entry analyzing this excerpt from page ${pageNumber}:\n\n"${sceneText.slice(
                    0,
                    800
                  )}"`,
                },
              ],
            },
          ],
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const text = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
        if (text) {
          saveCommentaryCache(pageNumber, text);
          return text;
        }
      }
    } catch (err) {
      console.warn('[GoogleGenAI] Direct Gemini chat failed:', err);
    }
  }

  // Fallback offline procedural log
  const proceduralLog = `DIAGNOSTIC LOG // PAGE ${pageNumber} // WARD, M.: Planck coordinate drift remains within 0.04%. The neuro-lattice collar is humming at 40.0Hz. Whatever sits in the unrendered margin between ticks, it hasn't blinked yet.`;
  saveCommentaryCache(pageNumber, proceduralLog);
  return proceduralLog;
}
